import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface OpenInNewTabDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  onOpenNewTab: () => void;
  title?: string;
  description?: string;
}

export default function OpenInNewTabDialog({
  isOpen,
  onCancel,
  onOpenNewTab,
  title,
  description,
}: OpenInNewTabDialogProps) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const dialog = (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2"
          >
            <path
              d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M15 3h6v6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 14L21 3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold mb-2 text-center">
          {title || t('open_in_new_tab_title', 'Open in new tab?')}
        </h3>
        <p className="text-sm text-gray-600 mb-4 text-center">
          {description ||
            t(
              'open_in_new_tab_description',
              'Safari requires a user click to open a new tab. Choose "Open in new tab" to continue.',
            )}
        </p>
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
          >
            {t('cancel', 'Cancel')}
          </button>
          <button
            onClick={onOpenNewTab}
            className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
          >
            {t('open', 'Open')}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
}
